import React, { useState, useMemo, useCallback } from 'react'

import FullDayRangePicker from '@components/shared/FullDayRangePicker'
import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import { useIsStandard } from '@contexts/operator/PlanContext'
import { pickingListService } from '@services/operator/pickingListService'
import { pickingListPresetService } from '@services/operator/pickingListPresetService'
import FulfillmentStatusLabel from '@shared/fulfillments/FulfillmentStatusLabel'
import ListPageHeader from '@shared/ListPageHeader'
import { MultipleValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule } from '@shared/table/TableIndex'
import { formatDateTime } from '@utils/dateTime'

import PickingListPresetConceptModal from '@shared/picking_lists/PickingListPresetConceptModal'
import { PlayCircleOutlined, ExclamationCircleOutlined, PlusOutlined, QuestionCircleFilled } from '@ant-design/icons'
import { Modal, Table, Button, Space, Tooltip, message, Spin, Empty, Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

interface PickingListPresetRow {
  id: string;
  name: string;
  merchant?: { id: string; name?: string } | null;
  warehouse?: { id: string; name?: string } | null;
  operator?: { id: string; name?: string } | null;
  priority?: number | null;
  picking_list_preset_schedules?: Array<{ id: string; execution_time: string }>;
  next_run_at?: string | null;
  [key: string]: unknown;
}

interface SimulateOrder {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface SimulateFulfillmentRow {
  id: string;
  name: string;
  status: string;
  merchant_name?: string;
  orders?: SimulateOrder[];
  total_quantity?: number;
  created_at?: string;
  [key: string]: unknown;
}

export default function OperatorPickingListPresetList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [lastModified, setLastModified] = useState(0)
  const [conceptVisible, setConceptVisible] = useState(false)
  const { merchants = [], warehouses = [] } = useOperatorData()
  const isStandard = useIsStandard()

  // Simulate modal state
  const [simulateModalOpen, setSimulateModalOpen] = useState(false)
  const [simulateLoading, setSimulateLoading] = useState(false)
  const [simulateFulfillments, setSimulateFulfillments] = useState<SimulateFulfillmentRow[]>([])
  const [simulatePresetName, setSimulatePresetName] = useState('')
  const [simulateMerchantId, setSimulateMerchantId] = useState<string | null>(null)

  const handleSimulate = useCallback(async (preset: PickingListPresetRow) => {
    setSimulatePresetName(preset.name)
    setSimulateMerchantId(preset.merchant?.id ?? null)
    setSimulateModalOpen(true)
    setSimulateLoading(true)
    setSimulateFulfillments([])

    try {
      const response = await (pickingListPresetService as any).simulate(preset.id)
      if (response.data) {
        setSimulateFulfillments(response.data)
      }
    } catch (error) {
      message.error(t('common.error.request_failed') as string)
    } finally {
      setSimulateLoading(false)
    }
  }, [t])

  const handleGeneratePickingList = useCallback(() => {
    if (simulateFulfillments.length === 0) return

    Modal.confirm({
      title: t('operator.picking_list_presets.simulate_modal.confirm_generate_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('operator.picking_list_presets.simulate_modal.confirm_generate_content', {
        count: simulateFulfillments.length
      }),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        try {
          const fulfillmentIds = simulateFulfillments.map(f => f.id)
          const response = await (pickingListService as any).create({
            fulfillment_ids: fulfillmentIds,
            merchant_id: simulateMerchantId,
          })

          if (response.success) {
            message.success(t('operator.picking_lists.create_success', { defaultValue: 'Picking list creation task started' }) as string)
            setSimulateModalOpen(false)
            // Optional: navigate to picking lists list or show job status
          } else {
            message.error((response.message || t('common.error.operation_failed')) as string)
          }
        } catch (error) {
          console.error('Generate picking list failed:', error)
          message.error(t('common.error.operation_failed') as string)
        }
      },
    })
  }, [simulateFulfillments, simulateMerchantId, t])

  const fulfillmentColumns = useMemo(() => [
    {
      title: t('models.fulfillment.name'),
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (name: string, record: SimulateFulfillmentRow) => (
        <Link to={`/fulfillments/${record.id}`} target="_blank">
          {name}
        </Link>
      ),
    },
    {
      title: t('models.fulfillment.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => <FulfillmentStatusLabel status={status} />,
    },
    // 單一 merchant 模式下不顯示 merchant 欄位
    ...(isStandard ? [] : [{
      title: t('models.merchant.name'),
      dataIndex: 'merchant_name',
      key: 'merchant_name',
      width: 120,
    }]),
    {
      title: t('models.order.name'),
      key: 'orders',
      width: 200,
      render: (_: unknown, record: SimulateFulfillmentRow) => (
        <Space direction="vertical" size={0}>
          {record.orders?.slice(0, 3).map((order) => (
            <span key={order.id} style={{ fontSize: 12 }}>{order.name}</span>
          ))}
          {(record.orders?.length ?? 0) > 3 && (
            <span style={{ fontSize: 12, color: '#999' }}>
              +{(record.orders?.length ?? 0) - 3} {t('common.more')}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: t('models.fulfillment.total_quantity'),
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      width: 80,
      align: 'center' as const,
    },
    {
      title: t('common.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => formatDateTime(date),
    },
  ], [t, isStandard])

  const columns = useMemo<unknown[]>(() => [
    {
      nameKey: `operator.picking_list_presets.list.name`,
      persist: true,
      sortable: true,
      sortFieldName: 'picking_list_presets.name',
      width: '200px',
      wrap: true,
      cell: ({ id, name }: PickingListPresetRow) => (
        <Link to={`/picking_lists/presets/${id}`}>
          {name}
        </Link>
      ),
    },
    // 單一 merchant 模式下不顯示 merchant 欄位
    ...(isStandard ? [] : [{
      nameKey: `models.merchant.name`,
      persist: true,
      sortable: true,
      sortFieldName: 'merchants.name',
      width: '150px',
      selector: ({ merchant }: PickingListPresetRow) => merchant?.name || '-',
    }]),
    {
      nameKey: `models.warehouse.name`,
      sortable: true,
      sortFieldName: 'warehouses.name',
      width: '150px',
      selector: ({ warehouse }: PickingListPresetRow) => warehouse?.name || '-',
    },
    {
      nameKey: `operator.picking_list_presets.list.creator`,
      width: '150px',
      selector: ({ operator }: PickingListPresetRow) => operator?.name || '-',
    },
    {
      nameKey: `operator.picking_list_presets.list.priority`,
      sortable: true,
      sortFieldName: 'picking_list_presets.priority',
      width: '100px',
      selector: ({ priority }: PickingListPresetRow) => priority ?? 0,
    },
    {
      nameKey: `operator.picking_list_presets.list.schedules`,
      width: '200px',
      wrap: true,
      cell: ({ picking_list_preset_schedules }: PickingListPresetRow) => (
        <Space size={[0, 4]} wrap>
          {picking_list_preset_schedules?.map((s) => (
            <Tag key={s.id}>{s.execution_time}</Tag>
          ))}
        </Space>
      ),
    },
    {
      nameKey: `operator.picking_list_presets.list.next_run_at`,
      width: '180px',
      selector: ({ next_run_at }: PickingListPresetRow) => (next_run_at ? formatDateTime(next_run_at) : '-'),
    },
    {
      nameKey: `common.actions`,
      persist: true,
      width: '80px',
      cell: (row: PickingListPresetRow) => (
        <Button
          type="text"
          icon={<PlayCircleOutlined />}
          onClick={(e) => {
            e.stopPropagation()
            handleSimulate(row)
          }}
          title={t('operator.picking_list_presets.simulate') as string}
        />
      ),
    },
  ], [t, handleSimulate, isStandard])

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t(`operator.picking_list_presets.list.name`),
      component: MultipleValue,
      inputName: 'picking_list_presets.name',
      defaultPinned: true,
      createable: true,
    },
    // 單一 merchant 模式下不顯示 merchant 篩選器
    ...(isStandard ? [] : [{
      label: t(`models.merchant._`),
      component: MultipleValue,
      inputName: 'picking_list_presets.merchant_id',
      createable: false,
      defaultOptions: merchants.map((merchant: { id: string; name: string }) => ({
        label: merchant.name,
        value: merchant.id
      })),
    }]),
    {
      label: t(`models.warehouse._`),
      component: MultipleValue,
      inputName: 'picking_list_presets.warehouse_id',
      createable: false,
      defaultOptions: warehouses ? warehouses.map((w: { id: string; name: string }) => ({
        label: w.name,
        value: w.id
      })) : [],
    },
    {
      label: t(`common.created_at`),
      component: FullDayRangePicker,
      sortable: false,
      inputName: 'picking_list_presets.created_at',
    },
  ], [t, merchants, warehouses, isStandard])

  return (
    <>
      <ListPageHeader
        items={[
          { label: t('operator.menu.picking.title') },
          { label: t('operator.menu.picking_lists.presets') },
        ]}
        guideButton={
          <Tooltip title={t('concepts.picking_list_presets.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/picking_lists/presets/new')}
          >
            {t('common.button.new')}
          </Button>
        }
      />
      <TableIndex
        columns={columns}
        searchable={{ match: 'contains', searchFields: [t('operator.picking_list_presets.list.name')] }}
        columnsEditable={true}
        tableId="operatorPickingListPresetsTable"
        defaultQuery={{
          sorting_field: 'picking_list_presets.created_at',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        service={pickingListPresetService as TableService}
      />

      <Modal
        title={t('operator.picking_list_presets.simulate_modal.title', { name: simulatePresetName })}
        open={simulateModalOpen}
        onCancel={() => setSimulateModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setSimulateModalOpen(false)}>
            {t('common.button.cancel')}
          </Button>,
          <Button
            key="submit"
            type="primary"
            disabled={simulateLoading || simulateFulfillments.length === 0}
            onClick={handleGeneratePickingList}
          >
            {t('operator.picking_list_presets.simulate_modal.generate_button', { defaultValue: 'Generate Picking List' })}
          </Button>,
        ]}
        width={900}
        centered
      >
        {simulateLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        ) : simulateFulfillments.length === 0 ? (
          <Empty description={t('operator.picking_list_presets.simulate_modal.no_results') as string} />
        ) : (
          <>
            <div style={{ marginBottom: 16, color: '#666' }}>
              {t('operator.picking_list_presets.simulate_modal.result_count', { count: simulateFulfillments.length })}
              {simulateFulfillments.length >= 100 && (
                <span style={{ marginLeft: 8, color: '#faad14' }}>
                  ({t('operator.picking_list_presets.simulate_modal.limit_warning')})
                </span>
              )}
            </div>
            <Table
              dataSource={simulateFulfillments}
              columns={fulfillmentColumns}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => t('common.total_count', { count: total }) as string }}
              size="small"
              scroll={{ x: 800 }}
            />
          </>
        )}
      </Modal>

      <PickingListPresetConceptModal
        visible={conceptVisible}
        onClose={() => setConceptVisible(false)}
      />
    </>
  )
}
